import Spinner from "components/Spinner";
import uuidv4 from "utils/uuidv4";
import { optionRequired } from "utils/form/validations";
import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import Input from "../Input";
import { ReactComponent as CarretIcon } from "assets/icons/select-carret.svg";
import * as S from "./InputSelect.style";

const InputSelect = ({
  initialValue,
  options = [],
  loading,
  onChange,
  validateOnBlur = true,
  validationRef,
  validationRules = [],
  half,
  width,
  noMinWidth,
  minSearch = 0,
  selectOnly,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef();
  const selectIdRef = useRef(uuidv4());
  const [displayTerm, setDisplayTerm] = useState("");
  const [value, setValue] = useState(initialValue?.id);

  useImperativeHandle(validationRef, () => ({
    ...(inputRef.current || {}),
    getData: () => options.find((o) => o.id === value),
    clear: () => {
      setIsOpen(false);
      setDisplayTerm("");
      setValue({ value: "" });
      inputRef?.current.update({ value: "" });
    },
  }));

  const avaliableOptions = useMemo(() => {
    if (selectOnly) {
      return options;
    }

    return (
      options?.filter(({ value: v }) =>
        v.toLowerCase().includes(displayTerm?.toLowerCase())
      ) || []
    );
  }, [displayTerm, options, selectOnly]);

  const onChangeHandler = (option) => {
    setValue(option.id);
    inputRef?.current.updateValue(option.value);
    setIsOpen(false);
    onChange?.(option.id);
  };

  const handleBlur = () => {
    const matchOption = options.find(({ value: v }) => v === displayTerm);
    if (matchOption) {
      setValue(matchOption.id);
    }
  };

  const initialInputValue = useMemo(
    () => options.find(({ id }) => id === initialValue?.id)?.value || "",
    [options, initialValue]
  );

  useEffect(() => {
    const initialV = options.find(({ id }) => id === initialValue?.id)?.value;
    if (initialV) {
      inputRef.current.updateValue(initialV);
    }
  }, [options, initialValue]);

  useEffect(() => {
    if (!!value) {
      setDisplayTerm(options.find(({ id }) => id === value)?.value || "");
    }
  }, [options, value]);

  useEffect(() => {
    const closeListener = (e) => {
      if (
        isOpen &&
        e.target.getAttribute("data-selectid") !== selectIdRef.current
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", closeListener);

    return () => document.removeEventListener("click", closeListener);
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const matchOption = options.find(({ value: v }) => v === displayTerm);
      if (matchOption) {
        setValue(matchOption.id);
      }
    }
  };

  const openOptions = (v = displayTerm) => {
    if (v?.length >= minSearch) {
      setIsOpen(true);
    }
  };

  return (
    <S.Input
      half={half}
      selectOnly={selectOnly}
      width={width}
      noMinWidth={noMinWidth}
    >
      <Input
        onClick={() => openOptions()}
        onFocus={() => openOptions()}
        onBlur={() => handleBlur()}
        onKeyDown={handleKeyDown}
        data-selectid={selectIdRef.current}
        initialValue={initialInputValue}
        disabled={loading}
        readOnly={selectOnly}
        noMinWidth={noMinWidth}
        {...props}
        onChange={(v) => {
          setValue("");
          setDisplayTerm(v);
          openOptions(v);
        }}
        validationRef={inputRef}
        validationRules={[...validationRules, optionRequired(value)]}
        validateOnBlur={false}
      />
      {selectOnly && !loading && <CarretIcon className="carret" />}
      {isOpen && (
        <>
          <S.OptionList shapeBorderRadius={null} shadowLevel={null}>
            {avaliableOptions?.map?.((option) => (
              <S.OptionItem
                value={option.id}
                key={option.id}
                onClick={() => onChangeHandler(option)}
              >
                {option.value}
              </S.OptionItem>
            ))}
          </S.OptionList>
        </>
      )}
      {loading && <Spinner className="loading-spinner" />}
    </S.Input>
  );
};

export default InputSelect;
